(() => {
  'use strict';
  let dispose;
  function init() {
    const panel = document.getElementById('HzHeaderSearchPanel');
    if (!panel || panel.dataset.liveSearch === 'ready') return;
    dispose?.();
    panel.dataset.liveSearch = 'ready';
    const input = panel.querySelector('input[type="search"]');
    const form = panel.querySelector('form');
    if (!input || !form) return;
    const events = new AbortController();
    const listen = (target, event, handler) => target.addEventListener(event, handler, { signal: events.signal });
    const popup = document.createElement('div');
    popup.className = 'hz-search-suggestions';
    popup.hidden = true;
    const list = document.createElement('div');
    list.id = 'HzSearchSuggestions';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Search suggestions');
    const status = document.createElement('p');
    status.className = 'hz-search-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    popup.append(status, list);
    panel.append(popup);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', list.id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('spellcheck', 'false');
    let timer, request, version = 0, active = -1;
    let links = [];
    const root = window.Shopify?.routes?.root || document.getElementById('HzApp')?.dataset.root || '/';
    const endpoint = new URL(window.routes?.predictive_search_url || root.replace(/\/?$/, '/') + 'search/suggest', location.origin);
    if (!endpoint.pathname.endsWith('.json')) endpoint.pathname += '.json';
    const currency = panel.dataset.currency || window.Shopify?.currency?.active || 'INR';
    let money;
    try { money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }); } catch { /* Price is optional. */ }
    function select(index) {
      active = index;
      links.forEach((link, i) => link.setAttribute('aria-selected', String(i === index)));
      if (links[index]) {
        input.setAttribute('aria-activedescendant', links[index].id);
        links[index].scrollIntoView({ block: 'nearest' });
      } else input.removeAttribute('aria-activedescendant');
    }
    function close() {
      clearTimeout(timer);
      request?.abort();
      version++;
      popup.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      list.removeAttribute('aria-busy');
      select(-1);
    }
    function safeLink(value) {
      try {
        const url = new URL(value, location.origin);
        return url.origin === location.origin && /^https?:$/.test(url.protocol) ? url.href : null;
      } catch { return null; }
    }
    function option(title, href, kind) {
      const target = safeLink(href);
      if (!target) return null;
      const a = document.createElement('a');
      a.href = target;
      a.className = 'hz-search-result ' + kind;
      a.id = 'HzSearchOption-' + links.length;
      a.setAttribute('role', 'option');
      a.setAttribute('aria-selected', 'false');
      a.setAttribute('aria-label', title);
      links.push(a);
      list.append(a);
      return a;
    }
    function text(tag, content, className) {
      const node = document.createElement(tag);
      node.textContent = content;
      if (className) node.className = className;
      return node;
    }
    function render(results, query, failed = false) {
      list.replaceChildren();
      links = [];
      select(-1);
      let count = 0;
      const products = Array.isArray(results.products) ? results.products.slice(0, 4) : [];
      const collections = Array.isArray(results.collections) ? results.collections.slice(0, 3) : [];
      for (const [items, label, kind] of [[products, 'Products', 'product'], [collections, 'Collections', 'collection']]) {
        if (!items.length) continue;
        const group = document.createElement('div');
        group.setAttribute('role', 'group');
        group.setAttribute('aria-label', label);
        group.append(text('p', label, 'hz-search-group-title'));
        list.append(group);
        for (const item of items) {
          const a = option(String(item.title || ''), item.url, kind);
          if (!a) continue;
          group.append(a);
          count++;
          if (kind === 'product') {
            const imageUrl = item.featured_image?.url || item.image;
            if (imageUrl) {
              try {
                const src = new URL(imageUrl, location.origin);
                if (/^https?:$/.test(src.protocol)) {
                  const img = document.createElement('img');
                  img.src = src.href;
                  img.alt = '';
                  img.width = 52;
                  img.height = 60;
                  a.append(img);
                }
              } catch { /* Keep the text suggestion if the image is invalid. */ }
            }
            const details = document.createElement('span');
            details.append(text('span', item.title, 'hz-search-result-title'));
            const price = Number(item.price);
            if (money && item.price != null && Number.isFinite(price)) details.append(text('span', money.format(price), 'hz-search-price'));
            if (item.available === false) details.append(text('span', 'Sold out', 'hz-search-stock'));
            a.append(details);
          } else a.append(text('span', item.title, 'hz-search-result-title'), text('span', '↗', 'hz-search-arrow'));
        }
      }
      status.textContent = failed ? 'Suggestions are unavailable. You can still search below.' : count ? count + (count === 1 ? ' suggestion for “' : ' suggestions for “') + query + '”' : 'No suggestions for “' + query + '”. Try another word or view all results.';
      const fullSearch = new URL(form.action, location.origin);
      fullSearch.searchParams.set('q', query);
      fullSearch.searchParams.set('options[prefix]', 'last');
      const all = option('View all results for “' + query + '”', fullSearch.href, 'hz-search-all');
      all?.append(text('span', 'View all results for “' + query + '”'), text('span', '→'));
      list.removeAttribute('aria-busy');
    }
    function search() {
      close();
      const query = input.value.trim();
      if (!query || document.activeElement !== input) return;
      const current = version;
      list.replaceChildren();
      links = [];
      popup.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      list.setAttribute('aria-busy', 'true');
      status.textContent = 'Searching…';
      timer = setTimeout(async () => {
        request = new AbortController();
        const url = new URL(endpoint);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        try {
          const response = await fetch(url, { signal: request.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Suggestions unavailable');
          const data = await response.json();
          if (current === version) render(data.resources?.results || {}, query);
        } catch (error) {
          if (current === version && error.name !== 'AbortError') render({}, query, true);
        }
      }, 220);
    }
    listen(input, 'input', event => { if (!event.isComposing) search(); });
    listen(input, 'compositionend', search);
    listen(input, 'focus', search);
    listen(input, 'keydown', event => {
      if (event.isComposing || popup.hidden) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
      else if (['ArrowDown', 'ArrowUp'].includes(event.key) && links.length) {
        event.preventDefault();
        select((active + (event.key === 'ArrowDown' ? 1 : active < 0 ? 0 : -1) + links.length) % links.length);
      } else if (event.key === 'Enter' && active >= 0 && links[active]) {
        event.preventDefault();
        links[active].click();
      }
    });
    listen(panel, 'focusout', event => { if (!panel.contains(event.relatedTarget)) close(); });
    listen(document, 'pointerdown', event => { if (!panel.contains(event.target)) close(); });
    listen(document, 'keydown', event => { if (event.key === 'Escape') close(); });
    listen(form, 'submit', close);
    dispose = () => { close(); events.abort(); popup.remove(); delete panel.dataset.liveSearch; };
  }
  init();
  document.addEventListener('shopify:section:load', init);
})();
